import ky, { HTTPError } from "ky";
import type { z } from "zod";
import { queryOptions } from "@tanstack/react-query";
import {
  apiErrorSchema,
  consentSchema,
  approvalSchema,
  aliasesSchema,
  identitySchema,
  inventorySchema,
  ruleSchema,
  serverSchema,
  stateSchema,
  type Rule,
  type Server,
} from "./schemas";

const transport = ky.create({
  credentials: "same-origin",
  timeout: 35_000,
  retry: { limit: 1, methods: ["get"] },
});
async function read<S extends z.ZodType>(path: string, schema: S): Promise<z.output<S>> {
  return schema.parse(await transport.get(path).json());
}
export const accessAPI = {
  consent: () => read("/api/device/consent", consentSchema),
  approveDevice: async (value: { csrf: string; approve: boolean }) =>
    approvalSchema.parse(await transport.post("/api/device/approve", { json: value }).json()),
  me: () => read("/api/me", identitySchema),
  state: () => read("/api/admin/state", stateSchema),
  inventory: () => read("/api/admin/netbird", inventorySchema),
  aliases: () => read("/api/aliases", aliasesSchema),
  saveServer: async (value: Server) =>
    serverSchema.parse(
      await transport.post("/api/admin/servers", { json: serverSchema.parse(value) }).json(),
    ),
  saveRule: async (value: Rule) =>
    ruleSchema.parse(
      await transport.post("/api/admin/rules", { json: ruleSchema.parse(value) }).json(),
    ),
  reconcile: () => transport.post("/api/admin/reconcile", { json: {} }).json(),
  logout: () => transport.post("/api/logout", { json: {} }),
};
export const queries = {
  consent: () =>
    queryOptions({ queryKey: ["device-consent"], queryFn: accessAPI.consent, retry: false }),
  me: () => queryOptions({ queryKey: ["identity"], queryFn: accessAPI.me, retry: false }),
  state: () =>
    queryOptions({
      queryKey: ["access", "state"],
      queryFn: accessAPI.state,
      refetchInterval: 15_000,
    }),
  inventory: () =>
    queryOptions({
      queryKey: ["access", "inventory"],
      queryFn: accessAPI.inventory,
      staleTime: 30_000,
    }),
  aliases: () => queryOptions({ queryKey: ["access", "aliases"], queryFn: accessAPI.aliases }),
};
export function isUnauthorized(error: unknown): boolean {
  return error instanceof HTTPError && error.response.status === 401;
}
export function errorMessage(error: unknown): string {
  if (error instanceof HTTPError) {
    const parsed = apiErrorSchema.safeParse(error.data);
    if (parsed.success) return parsed.data.error;
    if (
      typeof error.data === "string" &&
      error.data.length < 300 &&
      !error.data.trim().startsWith("<")
    )
      return error.data.trim();
    return error.response.status === 403
      ? "Your account needs permission for this action."
      : `Request failed (${error.response.status}). Refresh or check service health.`;
  }
  return error instanceof Error ? error.message : "The request could not be completed.";
}
